import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, Pencil, Plus, Star, Trash2, X } from 'lucide-react'
import {
  createGameAccount,
  deleteGameAccount,
  listGameAccounts,
  setDefaultGameAccount,
  updateGameAccount,
  type GameAccount,
} from './api'
import { useI18n } from '../../lib/i18n'
import { track } from '../../lib/analytics'
import { useAuth } from '../auth/AuthGate'

const ACTIVE_ACCOUNT_KEY = 'clash_cards_active_account_id'
export const ACTIVE_ACCOUNT_EVENT = 'clash-cards:account-changed'
export type ActiveAccountDetail = {
  id: string | null
  name?: string
  created?: boolean
  switched?: boolean
}

function announceAccount(id: string | null, detail: Omit<ActiveAccountDetail, 'id'> = {}) {
  window.dispatchEvent(
    new CustomEvent<ActiveAccountDetail>(ACTIVE_ACCOUNT_EVENT, { detail: { id, ...detail } }),
  )
}

export function AccountSwitcher() {
  const { locale, t } = useI18n()
  const { session, ready, openSignIn } = useAuth()
  const demoMode = new URLSearchParams(window.location.search).get('demo') === '1'
  const dialogRef = useRef<HTMLDialogElement>(null)
  const refreshGeneration = useRef(0)
  const [accounts, setAccounts] = useState<GameAccount[]>([])
  const [activeId, setActiveId] = useState(localStorage.getItem(ACTIVE_ACCOUNT_KEY))
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [displayName, setDisplayName] = useState('')
  const [playerTag, setPlayerTag] = useState('')
  const [regionCode, setRegionCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)

  const active =
    accounts.find((account) => account.id === activeId) ??
    accounts.find((account) => account.is_default) ??
    accounts[0]

  useEffect(() => {
    if (demoMode) {
      const now = new Date().toISOString()
      const demo: GameAccount = {
        id: 'demo',
        display_name: t('demoAccount'),
        player_tag: '#DEMO',
        region_code: 'CN',
        is_default: true,
        collected_count: 5,
        total_cards: 60,
        completion_percent: 8,
        created_at: now,
        updated_at: now,
      }
      setAccounts([demo])
      setActiveId('demo')
      setLoading(false)
      announceAccount('demo')
      return
    }
    if (!ready) return
    if (!session) {
      refreshGeneration.current++
      setAccounts([])
      setActiveId(null)
      setLoading(false)
      localStorage.removeItem(ACTIVE_ACCOUNT_KEY)
      announceAccount(null)
      return
    }
    void refresh()
  }, [demoMode, ready, session, t])

  async function refresh(preferredId?: string, created = false) {
    const generation = ++refreshGeneration.current
    setLoading(true)
    setError(null)
    try {
      const next = await listGameAccounts()
      if (generation !== refreshGeneration.current) return
      setAccounts(next)
      const preferred = preferredId ?? activeId
      const nextId = next.some((item) => item.id === preferred)
        ? preferred
        : (next.find((item) => item.is_default)?.id ?? next[0]?.id ?? null)
      setActiveId(nextId)
      if (nextId) localStorage.setItem(ACTIVE_ACCOUNT_KEY, nextId)
      else localStorage.removeItem(ACTIVE_ACCOUNT_KEY)
      announceAccount(nextId, {
        name: next.find((item) => item.id === nextId)?.display_name,
        created,
      })
      if (next.length === 0) window.setTimeout(() => dialogRef.current?.showModal(), 0)
    } catch {
      if (generation === refreshGeneration.current) setError(t('accountServiceUnavailable'))
    } finally {
      if (generation === refreshGeneration.current) setLoading(false)
    }
  }

  function selectAccount(account: GameAccount) {
    setActiveId(account.id)
    localStorage.setItem(ACTIVE_ACCOUNT_KEY, account.id)
    announceAccount(account.id, { name: account.display_name, switched: true })
    track('account_switched', locale, account.id)
    dialogRef.current?.close()
  }

  async function submitAccount(event: React.FormEvent) {
    event.preventDefault()
    if (demoMode) {
      setError(t('demoNoCreate'))
      return
    }
    setCreating(true)
    setError(null)
    try {
      const id = editingId
        ? (await updateGameAccount(editingId, { displayName, playerTag, regionCode }), editingId)
        : await createGameAccount({ displayName, playerTag, regionCode })
      if (!editingId) track('game_account_created', locale, id)
      setDisplayName('')
      setPlayerTag('')
      setRegionCode('')
      setEditingId(null)
      await refresh(id, !editingId)
      dialogRef.current?.close()
    } catch (cause) {
      const error = cause as { message?: string }
      setError(
        error.message?.includes('game_account_limit_reached')
          ? t('accountLimit')
          : t('createFailed'),
      )
    } finally {
      setCreating(false)
    }
  }

  function beginEdit(account: GameAccount) {
    setEditingId(account.id)
    setDisplayName(account.display_name)
    setPlayerTag(account.player_tag ?? '')
    setRegionCode(account.region_code ?? '')
  }

  async function makeDefault(id: string) {
    if (demoMode) return
    try {
      await setDefaultGameAccount(id)
      await refresh(id)
    } catch {
      setError(t('defaultFailed'))
    }
  }

  async function remove(id: string) {
    if (demoMode) {
      setError(t('demoNoDelete'))
      return
    }
    if (!window.confirm(t('deleteGameConfirm'))) return
    try {
      await deleteGameAccount(id)
      await refresh()
    } catch {
      setError(t('deleteGameFailed'))
    }
  }

  return (
    <>
      <button
        className="account-switcher"
        type="button"
        onClick={() => (session || demoMode ? dialogRef.current?.showModal() : openSignIn())}
      >
        <span className="account-symbol">
          {active?.display_name.slice(0, 1).toUpperCase() || 'A'}
        </span>
        <span>
          {loading
            ? t('loading')
            : !session && !demoMode
              ? t('guest')
              : active?.display_name || t('createAccount')}
        </span>
        <ChevronDown aria-hidden="true" />
      </button>

      <dialog className="account-dialog" ref={dialogRef}>
        <div className="dialog-heading">
          <div>
            <p className="eyebrow gold">{t('gameAccountsLabel')}</p>
            <h2>{t('chooseAccount')}</h2>
          </div>
          <button type="button" onClick={() => dialogRef.current?.close()} aria-label={t('close')}>
            <X />
          </button>
        </div>

        <div className="account-list">
          {accounts.map((account) => (
            <article key={account.id}>
              <button
                className="account-select"
                type="button"
                onClick={() => selectAccount(account)}
              >
                <span className="account-symbol">
                  {account.display_name.slice(0, 1).toUpperCase()}
                </span>
                <span>
                  <strong>{account.display_name}</strong>
                  <small>
                    {account.player_tag || t('noPlayerTag')} ·{' '}
                    {t('accountCompletion', { percent: account.completion_percent })}
                  </small>
                </span>
                {account.id === active?.id && <Check aria-label={t('currentAccount')} />}
              </button>
              <div className="account-actions">
                <button
                  type="button"
                  onClick={() => beginEdit(account)}
                  aria-label={t('edit', { name: account.display_name })}
                >
                  <Pencil />
                </button>
                <button
                  type="button"
                  onClick={() => makeDefault(account.id)}
                  aria-label={t('defaultAccount', { name: account.display_name })}
                  disabled={account.is_default}
                >
                  <Star />
                </button>
                <button
                  type="button"
                  onClick={() => remove(account.id)}
                  aria-label={t('delete', { name: account.display_name })}
                >
                  <Trash2 />
                </button>
              </div>
            </article>
          ))}
        </div>

        <form className="account-create" onSubmit={submitAccount}>
          {accounts.length === 0 && (
            <div className="onboarding-note">
              <strong>{t('firstAccount')}</strong>
              <p>{t('firstAccountHint')}</p>
            </div>
          )}
          <h3>
            <Plus aria-hidden="true" />
            {editingId ? t('editAccount') : t('addAccount')}
          </h3>
          <input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder={t('accountName')}
            maxLength={40}
            required
          />
          <input
            value={playerTag}
            onChange={(event) => setPlayerTag(event.target.value)}
            placeholder={t('playerTagOptional')}
            maxLength={40}
          />
          <input
            value={regionCode}
            onChange={(event) =>
              setRegionCode(
                event.target.value
                  .toUpperCase()
                  .replace(/[^A-Z]/g, '')
                  .slice(0, 2),
              )
            }
            placeholder={t('regionOptional')}
            pattern="[A-Z]{2}"
            maxLength={2}
          />
          <button type="submit" disabled={creating}>
            {creating ? t('savingAccount') : editingId ? t('saveChanges') : t('createAccount')}
          </button>
        </form>
        {error && (
          <p className="dialog-error" role="status">
            {error}
          </p>
        )}
      </dialog>
    </>
  )
}
